import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Play, Pause, ExternalLink } from "lucide-react";
import { Reveal, Overline } from "@/components/Reveal";
import RecordLogo from "@/components/brand/RecordLogo";
import { api } from "@/lib/api";

// The listening station. Provider-aware:
//   State A — no playable preview: record/artwork + LISTEN button (no fake play)
//   State B — official embed available: open the provider embed
//   State C — authorized direct preview: custom play spins the record
// No autoplay. Copyrighted audio is never self-hosted.
export default function SongOfWeek() {
  const [song, setSong] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [showEmbed, setShowEmbed] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    api
      .get("/song-of-the-week")
      .then((r) => setSong(r.song))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  const hasPreview = !!song?.preview_url;
  const hasEmbed = !!song?.embed_url;
  const listenUrl = song?.listen_url || song?.external_listen_url;
  const providerName = song?.provider
    ? song.provider.charAt(0).toUpperCase() + song.provider.slice(1)
    : "the source";

  const togglePlay = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) a.pause();
    else a.play().catch(() => {});
  };

  return (
    <section
      id="song-of-the-week"
      data-testid="song-of-the-week-section"
      className="relative overflow-hidden border-y border-corduroy bg-forest py-28 md:py-40"
    >
      <div className="glow-gold pointer-events-none absolute -left-40 top-1/2 h-[34rem] w-[34rem] -translate-y-1/2" aria-hidden="true" />
      <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 md:px-10 lg:grid-cols-12">
        {/* The record / album art */}
        <Reveal className="flex justify-center lg:col-span-5">
          <div className="relative">
            {loaded && song?.artwork_url ? (
              <div
                data-testid="sotw-artwork"
                className="record-shadow relative aspect-square w-[74vw] max-w-[380px] overflow-hidden rounded-full border border-gold/30"
              >
                <img
                  src={song.artwork_url}
                  alt={`${song.title} — ${song.artist}`}
                  className={`h-full w-full object-cover ${playing ? "animate-spin-slower" : ""}`}
                  style={{ willChange: "transform" }}
                />
                <span
                  className="pointer-events-none absolute left-1/2 top-1/2 h-[8%] w-[8%] -translate-x-1/2 -translate-y-1/2 rounded-full border border-gold/50 bg-ink"
                  aria-hidden="true"
                />
              </div>
            ) : (
              <RecordLogo
                size={380}
                spin={hasPreview ? playing : true}
                className="w-[74vw] max-w-[380px]"
                testid="sotw-record"
              />
            )}

            {/* State C — authorized preview → custom play control */}
            {hasPreview && (
              <>
                <button
                  type="button"
                  onClick={togglePlay}
                  data-testid="sotw-play-button"
                  aria-label={playing ? "Pause preview" : "Play preview"}
                  className="absolute left-1/2 top-1/2 flex h-16 w-16 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-ember text-ink shadow-2xl transition-colors hover:bg-neon hover:text-cream"
                >
                  {playing ? <Pause size={24} fill="currentColor" /> : <Play size={24} fill="currentColor" className="ml-1" />}
                </button>
                <audio
                  ref={audioRef}
                  src={song.preview_url}
                  preload="none"
                  onPlay={() => setPlaying(true)}
                  onPause={() => setPlaying(false)}
                  onEnded={() => setPlaying(false)}
                />
              </>
            )}
          </div>
        </Reveal>

        {/* The note + listen */}
        <Reveal delay={0.1} className="lg:col-span-7">
          <Overline>Music</Overline>
          {loaded && song ? (
            <div data-testid="song-of-the-week-card">
              <h2 className="mt-4 font-display text-4xl font-black italic leading-[0.92] tracking-tight text-cream md:text-6xl">
                &ldquo;{song.title}&rdquo;
              </h2>
              <p className="mt-3 font-editorial text-2xl italic text-gold md:text-3xl">
                {song.artist}
              </p>
              <div className="mt-6 h-px w-16 bg-gold/60" aria-hidden="true" />
              {song.note && (
                <p className="mt-6 max-w-xl font-editorial text-xl leading-relaxed text-cream/85 md:text-2xl">
                  {song.note}
                </p>
              )}

              <div className="mt-9 flex flex-wrap items-center gap-4">
                {listenUrl && (
                  <a
                    href={listenUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="sotw-listen-button"
                    className="inline-flex items-center gap-2 rounded-full bg-ember px-7 py-3.5 font-bold text-ink transition-colors hover:bg-neon hover:text-cream"
                  >
                    <ExternalLink size={16} /> Listen{song.provider ? ` on ${providerName}` : ""}
                  </a>
                )}
                {hasEmbed && (
                  <button
                    type="button"
                    onClick={() => setShowEmbed((v) => !v)}
                    data-testid="sotw-embed-toggle"
                    className="inline-flex items-center gap-2 rounded-full border border-gold/50 px-7 py-3.5 font-semibold text-gold transition-colors hover:bg-gold hover:text-ink"
                  >
                    {showEmbed ? "Hide player" : `Play here`}
                  </button>
                )}
                {song.related_program && (
                  <Link
                    to={`/episodes/${song.related_program}`}
                    data-testid="sotw-related-program"
                    className="font-sans text-[11px] font-bold uppercase tracking-[0.24em] text-cream/70 transition-colors hover:text-gold"
                  >
                    From the Program &rarr;
                  </Link>
                )}
              </div>

              {hasEmbed && showEmbed && (
                <div
                  data-testid="sotw-embed"
                  className="mt-8 overflow-hidden rounded-2xl border border-gold/25"
                >
                  <iframe
                    src={song.embed_url}
                    title={`${song.title} by ${song.artist}`}
                    className="h-[152px] w-full"
                    allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                    loading="lazy"
                  />
                </div>
              )}

              {song.week_of && (
                <p className="mt-8 font-sans text-[11px] font-bold uppercase tracking-[0.3em] text-cream/50">
                  {song.week_of}
                </p>
              )}
              <Link
                to="/music#record-box"
                data-testid="sotw-archive-link"
                className="mt-6 inline-block font-sans text-[11px] font-bold uppercase tracking-[0.24em] text-gold transition-opacity hover:opacity-70"
              >
                The Record Box &mdash; every pick &rarr;
              </Link>
            </div>
          ) : (
            <div data-testid="song-of-the-week-empty">
              <h2 className="mt-4 font-display text-4xl font-black leading-[0.92] tracking-tight text-cream md:text-6xl">
                The music lives here.
              </h2>
              <p className="mt-6 max-w-xl font-editorial text-xl leading-relaxed text-cream/85 md:text-2xl">
                I&rsquo;ve been making music longer than I&rsquo;ve been making
                this show. You&rsquo;ll find it here when it&rsquo;s ready.
              </p>
              <Link
                to="/music#record-box"
                data-testid="sotw-archive-link"
                className="mt-8 inline-block font-sans text-[11px] font-bold uppercase tracking-[0.24em] text-gold transition-opacity hover:opacity-70"
              >
                The Record Box &rarr;
              </Link>
            </div>
          )}
        </Reveal>
      </div>
    </section>
  );
}
